// Image export options (F20): sizes, entitlement gating, palettes and the attribution line.
import { BRAND } from '../brand';
import type { Tier } from '../entitlements/tier';

export type ResolutionId = '1080p' | '2k' | '4k';
export type Background = 'transparent' | 'light' | 'dark';

/** Sized by long edge; the short edge follows the visible view's aspect ratio. */
export const RESOLUTIONS: Record<ResolutionId, { label: string; long: number; tier: 'free' | 'supporter' }> = {
  '1080p': { label: '1080p', long: 1920, tier: 'free' },
  '2k': { label: '2K', long: 2560, tier: 'supporter' },
  '4k': { label: '4K', long: 3840, tier: 'supporter' },
};

/** Free users get 1080p; supporters and schools get every size (F13, F15). */
export function allowedResolutions(t: Tier): ResolutionId[] {
  return (Object.keys(RESOLUTIONS) as ResolutionId[]).filter((id) => RESOLUTIONS[id].tier === 'free' || t === 'supporter' || t === 'school');
}

export function sizeFor(id: ResolutionId, aspect: number): { width: number; height: number } {
  const long = RESOLUTIONS[id].long;
  return aspect >= 1 ? { width: long, height: Math.round(long / aspect) } : { width: Math.round(long * aspect), height: long };
}

/** Scales a request down to what the device can render, keeping the aspect ratio. */
export function fitSize(req: { width: number; height: number }, max: number): { width: number; height: number; scaled: boolean } {
  const k = Math.min(1, max / Math.max(req.width, req.height));
  if (k >= 1) return { ...req, scaled: false };
  return { width: Math.floor(req.width * k), height: Math.floor(req.height * k), scaled: true };
}

/** Export palettes. Mirrors the light and dark `--paper`/`--ink`/`--ink-soft` tokens in src/ui/tokens.css (checked by a test). */
export const PALETTES = {
  light: { bg: '#fbf8f2', ink: '#14323a', soft: '#3d5a61', accent: '#c4533a' },
  dark: { bg: '#0f1a1d', ink: '#e9f1ef', soft: '#b4c5c2', accent: '#f08a6f' },
} as const;

/** Which palette draws the footer and label for a background. Transparent images get the light strip. */
export function paletteFor(bg: Background) {
  return bg === 'dark' ? PALETTES.dark : PALETTES.light;
}

/** "marrow-atlas.netlify.app/s/heart": short, readable, and still a working link. */
export function shortUrl(origin: string, structureId?: string | null): string {
  const host = origin.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return structureId ? `${host}/s/${structureId}` : host;
}

/** The attribution line burned into every export. CC BY-SA requires it, so it can't be switched off (F09). */
export function footerText(url: string): string {
  return `${BRAND.name} · ${BRAND.attribution} · ${url}`;
}
